import { AxisTickProps } from '@nivo/axes'
import { Line, LineSvgProps } from '@nivo/line'
import { DSTooltip } from '@repo/design-system/index'
import { QIcon_question_outline } from '@repo/icons/index'
import { ACTIVE_CHART_COLOR, chartWidth } from '../../../../modules/consts/caf'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { n_caf } from '../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { ICON_QUESTION_GRAY } from '../../../../modules/svg/svg'
import { COLORS } from '../../../../modules/vars'
import { useWebviewModal } from '../common/Modal/WebviewModal'
import CommonLegendList, { LEGEND_SHAPE } from './common/CommonLegendList'
import { CustomXTick } from './common/CustomXYTick'

const theme = {
  grid: {
    line: {
      stroke: 'rgba(226, 233, 242, 1)',
      strokeWidth: 1,
      strokeDasharray: '2, 2', // 점선 스타일
    },
  },
}
const X_AXIS_VISIBLE_MAX = 26
const MAX_STANDARD_NUM = 180
const MIN_STANDARD_NUM = 120
const Y_MARKER_VALUE = 77
const MARKER_COLOR = 'rgba(255, 48, 81, 1)'
const Y_AXIS_VISIBLE_MAX = 500
const Y_AXIS_INTERVAL = 50
const LEFT_MARGIN = 45
const CHART_HEIGHT = 240

const CHART_STYLE: Partial<LineSvgProps> = {
  curve: 'linear',
  margin: {
    top: 12,
    left: 0,
    right: 5,
    bottom: 23,
  },
  role: 'presentation',
  colors: [ACTIVE_CHART_COLOR],
  enablePoints: true,
  pointBorderWidth: 2,
  pointColor: 'white',
  animate: true,
  pointSize: 7,
  gridYValues: Array(Math.ceil(Y_AXIS_VISIBLE_MAX / Y_AXIS_INTERVAL))
    .fill(0)
    .map((_, i) => (i + 1) * 50),
  gridXValues: [-1, 10, 20],
  theme: theme,
}

interface Props {
  data: any[]
  marker?: boolean
  dataKey: string
  indexBy: string
  options?: Partial<LineSvgProps>
  pl?: number
}
const SpeechRateChart = ({ data, marker, dataKey, indexBy, options = {}, pl = 0 }: Props) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const FLUENCY_LABEL_LIST = [
    { label: t(n_caf.fluency.graph_legend1), color: 'rgba(254, 246, 227, 1)', type: LEGEND_SHAPE.square },
    { label: t(n_caf.fluency.graph_legend2), color: 'rgba(63, 61, 136, 1)', type: LEGEND_SHAPE.round },
  ]
  const HIGHLIGHT_LABEL_LIST = [
    { label: t(n_caf.fluency.graph_legend4), color: COLORS.RED500, type: LEGEND_SHAPE.round },
    { label: t(n_caf.fluency.graph_legend2), color: 'rgba(63, 61, 136, 1)', type: LEGEND_SHAPE.line },
    { label: t(n_caf.fluency.graph_legend1), color: 'rgba(254, 246, 227, 1)', type: LEGEND_SHAPE.square },
    { label: t(n_caf.fluency.graph_legend3), color: COLORS.RED500, type: LEGEND_SHAPE.line },
  ]

  const webviewPopup = useWebviewModal()
  const handleClickTooltip = () => {
    if (isMobile) {
      webviewPopup.show({
        id: 'epc_popup',
        title: t(n_caf.fluency.s_modal_title),
        body: t(n_caf.fluency.s_modal_subtitle),
      })
    }
  }

  const graphData = [
    {
      id: 1,
      color: ACTIVE_CHART_COLOR,
      data:
        data.map((item) => {
          return {
            x: item[indexBy] || 0,
            y: Number(item[dataKey]?.toFixed(0)) || 0,
            color: item[dataKey] < Y_MARKER_VALUE ? MARKER_COLOR : ACTIVE_CHART_COLOR,
          }
        }) || [],
    },
  ]
  // X Axis
  // 데이터가 10개 미만인 경우 x축 라벨을 표시하지 않는다.
  if (!Array.isArray(graphData[0]?.data)) return null
  if (graphData[0]?.data.length === 0) return null

  const yRangeMax =
    Math.ceil(Math.max(...graphData[0].data.map((item) => item.y)) / Y_AXIS_INTERVAL) * Y_AXIS_INTERVAL >
    Y_AXIS_VISIBLE_MAX
      ? Y_AXIS_VISIBLE_MAX
      : Math.ceil(Math.max(...graphData[0].data.map((item) => item.y)) / Y_AXIS_INTERVAL) * Y_AXIS_INTERVAL
  const yRange = Array(yRangeMax / 50)
    .fill(0)
    .map((v, i) => (i + 1) * 50)
  const dataWidth = chartWidth - CHART_STYLE.margin.left - CHART_STYLE.margin.right
  const eachWidth = Math.floor(dataWidth / X_AXIS_VISIBLE_MAX)
  const width = isMobile ? eachWidth * data.length : eachWidth * data.length
  console.log(eachWidth, width, 'width!')
  return (
    <>
      {isMobile ? (
        <div className="flex h-[240px] w-full flex-row ">
          <div className={`h-full pl-[${pl}px] flex flex-col justify-between pr-[9px]  text-[12px] text-gray-500`}>
            {yRange?.reverse()?.map((y) => (
              <div key={y}>{y}</div>
            ))}
          </div>
          <div className="no-scrollbar overflow-x-auto overflow-y-hidden">
            <Line
              {...CHART_STYLE}
              height={CHART_HEIGHT}
              width={width}
              data={graphData}
              axisLeft={{
                tickSize: 0,
                tickValues: yRange,
                tickPadding: 20,
                renderTick: () => <></>,
              }}
              axisBottom={{
                tickSize: 0,
                legendPosition: 'start',
                tickPadding: 10,
                renderTick: (tick: AxisTickProps<string>) => <CustomXTick tick={tick} yGap={17} />,
              }}
              yScale={{ type: 'linear', min: Y_AXIS_INTERVAL, max: yRangeMax }}
              xScale={{
                type: 'linear',
                min: -1,
                max:
                  data.length <= 10
                    ? 10
                    : graphData[0]?.data?.length === X_AXIS_VISIBLE_MAX
                    ? X_AXIS_VISIBLE_MAX
                    : graphData[0]?.data?.length + 1,
              }}
              layers={[BGLayer, 'grid', 'markers', 'areas', 'lines', 'slices', 'points', 'legends', 'mesh', 'axes']}
              markers={
                marker
                  ? [
                      {
                        axis: 'y', // x축에 대한 마커
                        value: Y_MARKER_VALUE, // 마커의 위치 (x값)
                        lineStyle: { stroke: MARKER_COLOR, strokeWidth: 1 }, // 마커의 선 스타일
                      },
                    ]
                  : []
              }
              pointBorderColor={(object) => {
                if (!marker) return ACTIVE_CHART_COLOR
                const y = graphData[0]?.data[object.index]?.y
                return y ? (y < Y_MARKER_VALUE ? MARKER_COLOR : ACTIVE_CHART_COLOR) : ACTIVE_CHART_COLOR
              }}
              {...options}
            />
          </div>
        </div>
      ) : (
        <div className="flex h-[240px] w-full flex-row ">
          <div className={`h-full pl-[${pl}px] flex flex-col justify-between pr-[9px]  text-[12px] text-gray-400`}>
            {yRange?.reverse()?.map((y) => (
              <div key={y}>{y}</div>
            ))}
          </div>
          <div className="no-scrollbar overflow-x-auto overflow-y-hidden">
            <Line
              {...CHART_STYLE}
              height={CHART_HEIGHT}
              width={width}
              data={graphData}
              axisLeft={{
                tickSize: 0,
                tickValues: yRange,
                tickPadding: 20,
                renderTick: () => <></>,
              }}
              axisBottom={{
                tickSize: 0,
                legendPosition: 'start',
                tickPadding: 10,
                renderTick: (tick: AxisTickProps<string>) => (
                  <CustomXTick tick={tick} yGap={17} color={COLORS.GRAY400} />
                ),
              }}
              yScale={{ type: 'linear', min: Y_AXIS_INTERVAL, max: yRangeMax }}
              xScale={{
                type: 'linear',
                min: -1,
                max:
                  data.length <= 10
                    ? 10
                    : graphData[0]?.data?.length === X_AXIS_VISIBLE_MAX
                    ? X_AXIS_VISIBLE_MAX
                    : graphData[0]?.data?.length + 1,
              }}
              layers={[BGLayer, 'grid', 'markers', 'areas', 'lines', 'slices', 'points', 'legends', 'mesh', 'axes']}
              markers={
                marker
                  ? [
                      {
                        axis: 'y', // x축에 대한 마커
                        value: Y_MARKER_VALUE, // 마커의 위치 (x값)
                        lineStyle: { stroke: MARKER_COLOR, strokeWidth: 1 }, // 마커의 선 스타일
                      },
                    ]
                  : []
              }
              pointBorderColor={(object) => {
                if (!marker) return ACTIVE_CHART_COLOR
                const y = graphData[0]?.data[object.index]?.y
                return y ? (y < Y_MARKER_VALUE ? MARKER_COLOR : ACTIVE_CHART_COLOR) : ACTIVE_CHART_COLOR
              }}
              {...options}
            />
          </div>
        </div>
      )}
      {isMobile ? (
        <div
          className="text-h5 mt-[5px] flex items-center justify-center gap-[6px] text-center font-medium tracking-tight text-gray-600"
          onClick={handleClickTooltip}
        >
          {t(n_caf.fluency.graph_sentence)}
          <img src={ICON_QUESTION_GRAY} width={16} height={16} alt="question" />
        </div>
      ) : (
        <div className="mt-[8px] flex flex-row items-center justify-center">
          <div className="flex cursor-pointer flex-row items-center justify-start">
            <span className="mr-[2px] text-[14px] text-gray-600">{t(n_caf.fluency.graph_sentence)}</span>
            <DSTooltip
              place="bottom"
              isMultiline={true}
              isOpenOnClick={true}
              triggerComponent={
                <QIcon_question_outline fill={COLORS.GRAY600} width={16} height={16} className="mb-[1px]" />
              }
              contentComponent={t(n_caf.fluency.s_modal_subtitle)}
            />
          </div>
        </div>
      )}

      {/* legend - 개별 하이라이트 : Fluency 상세 페이지 */}
      <CommonLegendList list={marker ? HIGHLIGHT_LABEL_LIST : FLUENCY_LABEL_LIST} />
    </>
  )
}

const BGLayer = (props) => {
  return (
    <g className="z-0" style={{ zIndex: 0 }}>
      <rect
        y={props.yScale(MAX_STANDARD_NUM)}
        width={props.width}
        height={props.yScale(MIN_STANDARD_NUM) - props.yScale(MAX_STANDARD_NUM)}
        fill="rgba(246, 195, 67, 0.15)"
      />
    </g>
  )
}

export default SpeechRateChart
